import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Page Login / Inscription — POC Phase 4
 * URL : /plateforme/login
 * UI seulement (pas de backend connecté). Toggle entre Connexion et Inscription.
 */
export default function PlateformeLogin() {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.title = "Connexion · Institut Impact Élite";
    const linkPreconn = document.createElement("link");
    linkPreconn.rel = "preconnect";
    linkPreconn.href = "https://fonts.googleapis.com";
    document.head.appendChild(linkPreconn);
    const linkFonts = document.createElement("link");
    linkFonts.rel = "stylesheet";
    linkFonts.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;500;700&display=swap";
    document.head.appendChild(linkFonts);
    return () => {
      document.head.removeChild(linkPreconn);
      document.head.removeChild(linkFonts);
    };
  }, []);

  const baseStyle = {
    fontFamily: "Inter, system-ui, sans-serif",
    background: "#0d0d0d",
    color: "#f5f5f5",
    minHeight: "100vh",
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    // POC : pas de backend, on simule un délai et on dirige vers le dashboard
    setTimeout(() => {
      setLoading(false);
      setMessage(
        mode === "login"
          ? "✅ POC : connexion simulée. Le backend auth sera connecté en Phase 5."
          : "✅ POC : inscription simulée. Tu recevras un courriel de confirmation à " + email + " (simulé)."
      );
    }, 800);
  };

  return (
    <div style={baseStyle} data-testid="plateforme-login">
      <style>{`
        .editorial { font-family: 'Playfair Display', serif; }
        .label { letter-spacing: 0.4em; font-size: 11px; }
        .frame { border: 1px solid rgba(201, 169, 97, 0.25); }
        .divider-gold { background: linear-gradient(90deg, transparent, #C9A961 30%, #C9A961 70%, transparent); height: 1px; }
        .gold { color: #C9A961; }
        .red-c { color: #C8102E; }
        .bg-red-c { background: #C8102E; }
        .bg-gold { background: #C9A961; }
        .btn-primary { background: #C8102E; color: #fff; transition: 0.3s; }
        .btn-primary:hover { background: #fff; color: #C8102E; }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-secondary { color: #C9A961; border: 1px solid #C9A961; transition: 0.3s; background: transparent; }
        .btn-secondary:hover { background: #C9A961; color: #0d0d0d; }
        .pf-link { transition: color 0.2s; }
        .pf-link:hover { color: #C9A961; }
        .input-pf {
          background: #161616;
          border: 1px solid rgba(201, 169, 97, 0.25);
          color: #f5f5f5;
          padding: 14px 18px;
          width: 100%;
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          transition: border 0.2s;
        }
        .input-pf:focus {
          border-color: #C9A961;
          outline: none;
        }
        .input-pf::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }
        .toggle-tab {
          padding: 10px 24px;
          font-size: 11px;
          letter-spacing: 0.3em;
          cursor: pointer;
          transition: all 0.2s;
        }
        .toggle-tab.active {
          background: #C9A961;
          color: #0d0d0d;
          font-weight: 700;
        }
        .toggle-tab.inactive {
          color: rgba(255, 255, 255, 0.5);
        }
        .toggle-tab.inactive:hover {
          color: #C9A961;
        }
      `}</style>

      {/* === NAV === */}
      <nav className="px-10 py-6 flex items-center justify-between"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <Link to="/plateforme" className="flex items-center gap-4" data-testid="nav-back">
          <div className="relative">
            <div className="w-12 h-12 bg-red-c flex items-center justify-center">
              <span className="editorial text-white text-2xl font-black">I</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gold"></div>
          </div>
          <div>
            <div className="label gold">— INSTITUT —</div>
            <div className="editorial text-2xl font-bold leading-none mt-0.5">Impact Élite</div>
          </div>
        </Link>
        <Link to="/plateforme" className="pf-link text-sm">← Retour à l'accueil</Link>
      </nav>

      {/* === FORM CARD === */}
      <section className="px-6 py-16 flex items-center justify-center" style={{ minHeight: "calc(100vh - 90px)" }}>
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="label red-c mb-4">— ACCÈS MEMBRE —</div>
            <h1 className="editorial font-black text-4xl md:text-5xl mb-3" data-testid="login-title">
              {mode === "login" ? (
                <>Bon retour parmi <span className="italic gold">les nôtres.</span></>
              ) : (
                <>Rejoins <span className="italic gold">l'Institut.</span></>
              )}
            </h1>
            <p className="text-white/60 text-sm leading-relaxed">
              {mode === "login"
                ? "Reprends ton parcours là où tu l'as laissé."
                : "Crée ton compte. Choisis ton cycle. Commence ton ascension."}
            </p>
          </div>

          {/* Toggle Login/Signup */}
          <div className="frame mb-8 flex" data-testid="auth-mode-toggle">
            <button
              onClick={() => setMode("login")}
              className={`flex-1 toggle-tab ${mode === "login" ? "active" : "inactive"}`}
              data-testid="toggle-login"
            >
              CONNEXION
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 toggle-tab ${mode === "signup" ? "active" : "inactive"}`}
              data-testid="toggle-signup"
            >
              INSCRIPTION
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <label className="label gold block mb-2">— NOM COMPLET —</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-pf"
                  placeholder="Jean Tremblay"
                  required
                  data-testid="input-name"
                />
              </div>
            )}
            <div>
              <label className="label gold block mb-2">— COURRIEL —</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-pf"
                placeholder="ton@courriel.com"
                required
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="label gold block mb-2">— MOT DE PASSE —</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-pf"
                placeholder="••••••••"
                required
                minLength={8}
                data-testid="input-password"
              />
              {mode === "signup" && (
                <div className="text-xs text-white/40 mt-2">Minimum 8 caractères. Solidité = engagement.</div>
              )}
            </div>

            {message && (
              <div className="frame p-4 text-sm" style={{ background: "#161616" }} data-testid="auth-message">
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-4 font-bold tracking-[0.3em] text-xs"
              data-testid="auth-submit"
            >
              {loading ? "Patiente…" : (mode === "login" ? "ENTRER →" : "CRÉER MON COMPTE →")}
            </button>
          </form>

          {/* Divider */}
          <div className="divider-gold my-10"></div>

          {/* Footer Links */}
          <div className="text-center space-y-3 text-sm">
            {mode === "login" ? (
              <>
                <Link to="#" className="block pf-link" data-testid="link-forgot">Mot de passe oublié?</Link>
                <div className="text-white/50">
                  Pas encore membre?{" "}
                  <button onClick={() => setMode("signup")} className="gold underline" data-testid="link-to-signup">Inscris-toi</button>
                </div>
              </>
            ) : (
              <div className="text-white/50">
                Déjà membre?{" "}
                <button onClick={() => setMode("login")} className="gold underline" data-testid="link-to-login">Connecte-toi</button>
              </div>
            )}
          </div>

          {/* Compliance */}
          <div className="mt-12 text-center">
            <div className="text-[10px] text-white/30 tracking-widest leading-relaxed max-w-xs mx-auto">
              EN UTILISANT CE SITE, TU ACCEPTES NOS CONDITIONS D'UTILISATION ET NOTRE POLITIQUE DE CONFIDENTIALITÉ (LOI 25 · QUÉBEC).
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
